import React, { useCallback, useMemo } from 'react';
import { View, TouchableOpacity, Image } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Text } from '../../../components/ui/AppText';
import { styles } from './styles';
import { extractYoutubeId, getYoutubeThumbnail } from '../../../utils/community/youtube';

const isYouTubeUrl = (url) =>
  !!url && (url.includes('youtube.com') || url.includes('youtu.be'));

// Resolve a melhor thumbnail disponível para o tipo do post:
// - vídeo YouTube → derivada do videoId
// - vídeo galeria → image_url (miniatura uploaded no momento do post)
// - imagem        → image_url do próprio post
const resolveThumbnail = (post) => {
  if (post.postType === 'video') {
    if (isYouTubeUrl(post.videoUrl)) {
      return getYoutubeThumbnail(extractYoutubeId(post.videoUrl));
    }
    return post.imageUrl || null;
  }
  if (post.postType === 'image') return post.imageUrl || null;
  return null;
};

/**
 * Card enxuto de post para a aba "Posts" do perfil (read-model dedicado).
 * Sem ações de feed (votar/denunciar): apagar aparece só para o dono (isOwner).
 * Layout adaptativo: posts de mídia mostram thumb 16:9 à esquerda.
 */
const ProfilePostCard = ({ post, colors, isOwner, onDelete, onPress }) => {
  const handleDelete = useCallback(() => onDelete?.(post), [onDelete, post]);
  const thumbnail = useMemo(() => resolveThumbnail(post), [post]);
  const isMedia = post.postType === 'video' || post.postType === 'image';
  // Post otimista (recém-criado, id temporário): o optimistic insert injeta no
  // cache posts() que inclui a lista do perfil. Esmaece e bloqueia press/delete
  // até a reconciliação — evita abrir/apagar um post de id __optimistic_*.
  const isOptimistic = post.__isOptimistic;

  return (
    <TouchableOpacity
      style={[styles.card, { backgroundColor: colors?.bgCard || '#1A1D26' }, isOptimistic && styles.optimistic]}
      activeOpacity={onPress && !isOptimistic ? 0.85 : 1}
      onPress={onPress && !isOptimistic ? () => onPress(post) : undefined}
      disabled={!onPress || isOptimistic}
    >
      <View style={styles.postHeaderRow}>
        <View style={[styles.topicBadge, { backgroundColor: `${post.topicColor || '#5B8FF9'}22` }]}>
          <Text style={styles.topicIcon}>{post.topicIcon || '📝'}</Text>
          <Text style={[styles.topicText, { color: post.topicColor || '#5B8FF9' }]} numberOfLines={1}>
            {post.topic || 'Geral'}
          </Text>
        </View>

        {isOwner && !isOptimistic && (
          <TouchableOpacity
            style={styles.deleteBtn}
            onPress={handleDelete}
            hitSlop={10}
            accessibilityRole="button"
            accessibilityLabel="Apagar post"
          >
            <Ionicons name="trash-outline" size={18} color="#FF6B6B" />
          </TouchableOpacity>
        )}
      </View>

      {isMedia ? (
        <View style={styles.mediaRow}>
          <View style={styles.mediaThumbWrap}>
            {thumbnail ? (
              <Image source={{ uri: thumbnail }} style={styles.mediaThumbImg} resizeMode="cover" />
            ) : (
              <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                <Ionicons
                  name={post.postType === 'video' ? 'videocam' : 'image'}
                  size={20}
                  color="rgba(255,255,255,0.5)"
                />
              </View>
            )}
            {post.postType === 'video' && (
              <View style={styles.mediaTypeBadge}>
                <Ionicons name="play" size={8} color="#FFFFFF" />
                <Text style={styles.mediaTypeBadgeText}>VÍDEO</Text>
              </View>
            )}
          </View>

          <View style={styles.mediaTextCol}>
            <Text style={[styles.postTitle, { color: colors?.textPrimary || '#FFFFFF' }]} numberOfLines={2}>
              {post.title}
            </Text>
            {!!post.body && (
              <Text style={[styles.postBody, { color: colors?.textSecondary || '#A0A3B1' }]} numberOfLines={2}>
                {post.body}
              </Text>
            )}
            <Text style={[styles.postMeta, { color: colors?.textTertiary || '#6B6E7B' }]}>
              ▲ {post.votes ?? 0} · 💬 {post.comments ?? 0} · {post.time}
            </Text>
          </View>
        </View>
      ) : (
        <>
          <Text style={[styles.postTitle, { color: colors?.textPrimary || '#FFFFFF' }]} numberOfLines={2}>
            {post.title}
          </Text>
          {!!post.body && (
            <Text style={[styles.postBody, { color: colors?.textSecondary || '#A0A3B1' }]} numberOfLines={2}>
              {post.body}
            </Text>
          )}
          <Text style={[styles.postMeta, { color: colors?.textTertiary || '#6B6E7B' }]}>
            ▲ {post.votes ?? 0} · 💬 {post.comments ?? 0} · {post.time}
          </Text>
        </>
      )}
    </TouchableOpacity>
  );
};

export default React.memo(ProfilePostCard);
